import { useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { assignUserToCustomer } from "../services/userService";
import { UserGroup } from "../../../types/UserGroup";
import { debug } from "../../../utils/debugLogger";

interface UseUserAssignmentReturn {
  assignUser: (userId: string, customerId: string, userGroupStr: string | string[] | any) => Promise<void>;
  loading: boolean;
  error: string | null;
  success: boolean;
}

const useUserAssignment = (): UseUserAssignmentReturn => {
  const { idToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const assignUser = async (userId: string, customerId: string, userGroupStr: string | any) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);
      
      // Debug log to see what's coming in
      debug.log("Original userGroup value:", userGroupStr);
      debug.log("userGroup type:", typeof userGroupStr);
      
      // Normalize the group to ensure it's a string
      let normalizedGroup: string;
      
      if (Array.isArray(userGroupStr)) {
        debug.log("Group is an array, taking first value");
        normalizedGroup = userGroupStr[0] || "";
      } else if (typeof userGroupStr === 'string') {
        debug.log("Group is already a string");
        normalizedGroup = userGroupStr;
      } else {
        normalizedGroup = "";
      }

      if (!normalizedGroup) {
        throw new Error("Role is required. Please select a role before assigning.");
      }

      debug.log("Normalized group:", normalizedGroup);
      
      // Now pass the normalized string value
      await assignUserToCustomer(userId, customerId, normalizedGroup, idToken);
      setSuccess(true);
    } catch (err: any) {
      debug.error("Error assigning user:", err);
      setError(err.message || "Failed to assign user to customer");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    assignUser,
    loading,
    error,
    success
  };
};

export default useUserAssignment;